import { Receipt, ShieldCheck, FileText, IdCard, Download, Clock } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

const openDocument = async (doc) => {
  if (!doc.url) return;
  try {
    // /api/client/files/... peut être fetché avec le cookie client_token
    if (doc.url.startsWith("/api/client/files/")) {
      const res = await fetch(`${BASE}${doc.url}`, { credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } else {
      window.open(doc.url, "_blank");
    }
  } catch (e) {
    console.error(e);
  }
};

const ICONS = {
  receipt: Receipt,
  shield: ShieldCheck,
  file: FileText,
  "shield-check": ShieldCheck,
  id: IdCard,
};

const STATUS_META = {
  signed:    { label: "Disponible", available: true },
  available: { label: "Disponible", available: true },
  pending:   { label: "En attente", available: false },
  upcoming:  { label: "À venir",    available: false },
};

const DocRow = ({ doc }) => {
  const Icon = ICONS[doc.icon] || FileText;
  const sm = STATUS_META[doc.status] || STATUS_META.upcoming;
  const disabled = !sm.available || !doc.url;

  return (
    <li
      data-testid={`client-doc-${doc.id}`}
      className={[
        "rounded-2xl bg-white border border-[#EEF2F5] p-4 flex items-center gap-4",
        disabled ? "opacity-60" : "",
      ].join(" ")}
    >
      <div
        className={[
          "h-12 w-12 rounded-2xl flex items-center justify-center shrink-0",
          disabled ? "bg-[#F3F4F6] text-[#9CA3AF]" : "bg-[#111111] text-white",
        ].join(" ")}
      >
        <Icon size={18} strokeWidth={1.75} />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-[14.5px] font-semibold text-[#111111] tracking-tight truncate">
          {doc.name}
        </p>
        <p className="mt-0.5 text-[11.5px] text-[#6B7280] flex items-center gap-1.5">
          {!sm.available && <Clock size={10} strokeWidth={2} />}
          {doc.date_label || sm.label}
        </p>
      </div>

      <button
        data-testid={`client-doc-download-${doc.id}`}
        onClick={() => openDocument(doc)}
        disabled={disabled}
        className={[
          "h-10 w-10 rounded-full flex items-center justify-center shrink-0 transition-colors",
          disabled
            ? "bg-transparent text-[#D1D5DB] cursor-not-allowed"
            : "bg-[#F7F8FA] hover:bg-[#EEF2F5] text-[#111111]",
        ].join(" ")}
        aria-label="Télécharger"
      >
        <Download size={16} strokeWidth={2} />
      </button>
    </li>
  );
};

const ClientDocuments = ({ documents = [] }) => {
  const available = documents.filter((d) => STATUS_META[d.status]?.available);
  const upcoming = documents.filter((d) => !STATUS_META[d.status]?.available);

  return (
    <section data-testid="client-documents" className="wyvio-fade-in">
      <h2 className="text-[26px] font-semibold tracking-tight text-[#111111]">
        Documents
      </h2>
      <p className="mt-1.5 text-[13.5px] text-[#6B7280] leading-relaxed">
        Retrouvez ici tous les documents liés à votre commande.
      </p>

      {documents.length === 0 && (
        <div className="mt-6 rounded-2xl bg-white border border-[#EEF2F5] p-8 text-center">
          <p className="text-[13.5px] font-medium text-[#111111]">
            Aucun document pour l'instant
          </p>
        </div>
      )}

      {available.length > 0 && (
        <ul className="mt-6 space-y-2.5">
          {available.map((d) => (
            <DocRow key={d.id} doc={d} />
          ))}
        </ul>
      )}

      {upcoming.length > 0 && (
        <>
          <p className="mt-8 text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider">
            À venir
          </p>
          <ul className="mt-3 space-y-2.5">
            {upcoming.map((d) => (
              <DocRow key={d.id} doc={d} />
            ))}
          </ul>
        </>
      )}
    </section>
  );
};

export default ClientDocuments;
